import React, { useState, useEffect, useRef, useCallback } from 'react';
import { X } from 'lucide-react';

/**
 * REST MODE - Módulo de descanso consciente
 * 
 * Soporta 4 programas:
 * - respiracion_4_6: Guía de respiración 4s inhalar, 6s exhalar
 * - silencio: Pantalla minimalista sin guías
 * - escaneo_corporal: Frases de relajación corporal
 * - mentes_inquietas: Modo ultra-simple para personas con dificultad para parar
 */

const RestMode = ({ 
  seconds = 60, 
  onComplete, 
  onSkip,
  exerciseName = '',
  seriesNumber = 1,
  nextSeriesNumber = 2,
  program = 'respiracion_4_6' // 'respiracion_4_6' | 'silencio' | 'escaneo_corporal' | 'mentes_inquietas'
}) => {
  const [timeLeft, setTimeLeft] = useState(seconds);
  const [isActive, setIsActive] = useState(true);
  const [breathPhase, setBreathPhase] = useState('inhale');
  const [breathCycle, setBreathCycle] = useState(0);
  const [skippedEarly, setSkippedEarly] = useState(false);
  const [interactions, setInteractions] = useState(0);
  const [currentBodyPart, setCurrentBodyPart] = useState(0);
  const startTimeRef = useRef(Date.now());
  const intervalRef = useRef(null);
  const breathIntervalRef = useRef(null);
  const bodyPartIntervalRef = useRef(null);

  // Configuración de respiración: 4s inhalar, 6s exhalar
  const INHALE_DURATION = 4000;
  const EXHALE_DURATION = 6000;
  const BREATH_CYCLE = INHALE_DURATION + EXHALE_DURATION;

  // Frases para escaneo corporal
  const BODY_SCAN_PHRASES = [
    { part: 'Mandíbula', action: 'Suelta la mandíbula' },
    { part: 'Hombros', action: 'Baja los hombros' },
    { part: 'Manos', action: 'Afloja las manos' },
    { part: 'Abdomen', action: 'Suelta el abdomen' },
    { part: 'Piernas', action: 'Relaja las piernas' },
  ];

  // Temporizador principal
  useEffect(() => {
    if (isActive && timeLeft > 0) {
      intervalRef.current = setInterval(() => {
        setTimeLeft(prev => {
          if (prev <= 1) {
            setIsActive(false);
            setTimeout(() => {
              logRestSession(false);
              onComplete?.();
            }, 500);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }

    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [isActive, timeLeft, onComplete]);

  // Ciclo de respiración (solo para respiracion_4_6 y mentes_inquietas)
  useEffect(() => {
    if (isActive && (program === 'respiracion_4_6' || program === 'mentes_inquietas')) {
      setBreathPhase('inhale');
      
      const runBreathCycle = () => {
        setBreathPhase('inhale');
        
        setTimeout(() => {
          if (isActive) {
            setBreathPhase('exhale');
            setBreathCycle(prev => prev + 1);
          }
        }, INHALE_DURATION);
      };

      runBreathCycle();
      breathIntervalRef.current = setInterval(runBreathCycle, BREATH_CYCLE);

      return () => {
        if (breathIntervalRef.current) clearInterval(breathIntervalRef.current);
      };
    }
  }, [isActive, program]);

  // Ciclo de escaneo corporal
  useEffect(() => {
    if (isActive && program === 'escaneo_corporal') {
      bodyPartIntervalRef.current = setInterval(() => {
        setCurrentBodyPart(prev => (prev + 1) % BODY_SCAN_PHRASES.length);
      }, 8000); // Cambiar cada 8 segundos

      return () => {
        if (bodyPartIntervalRef.current) clearInterval(bodyPartIntervalRef.current);
      };
    }
  }, [isActive, program]);

  const handleInteraction = useCallback(() => {
    setInteractions(prev => prev + 1);
  }, []);

  const logRestSession = useCallback(async (abandoned) => {
    const actualTime = Math.round((Date.now() - startTimeRef.current) / 1000);
    
    const restData = {
      programmed_time: seconds,
      actual_time: actualTime,
      abandoned_early: abandoned,
      interactions: interactions,
      exercise_name: exerciseName,
      series_completed: seriesNumber,
      program: program
    };

    try {
      const existingLogs = JSON.parse(localStorage.getItem('rest_logs') || '[]');
      existingLogs.push({
        ...restData,
        timestamp: new Date().toISOString()
      });
      localStorage.setItem('rest_logs', JSON.stringify(existingLogs));
    } catch (e) {
      console.error('Error saving rest log:', e);
    }
  }, [seconds, interactions, exerciseName, seriesNumber, program]);

  const handleSkip = () => {
    setSkippedEarly(true);
    setIsActive(false);
    logRestSession(true);
    onSkip?.();
  };

  const formatTime = (secs) => {
    const mins = Math.floor(secs / 60);
    const remainingSecs = secs % 60;
    if (mins > 0) {
      return `${mins}:${remainingSecs.toString().padStart(2, '0')}`;
    }
    return `${remainingSecs}`;
  };

  const progress = ((seconds - timeLeft) / seconds) * 100;

  // ============== RENDERIZADO POR PROGRAMA ==============

  // PROGRAMA: SILENCIO
  const renderSilencio = () => (
    <div className="relative z-10 flex flex-col items-center justify-center w-full max-w-md px-6">
      {/* Temporizador minimalista */}
      <div className="mb-12">
        <span className="text-7xl font-extralight text-zinc-400 tabular-nums">
          {formatTime(timeLeft)}
        </span>
      </div>

      {/* Mensaje único */}
      <p className="text-zinc-600 text-lg font-light tracking-wide mb-16">
        Descansa. Recupera.
      </p>

      {/* Botón saltar */}
      <button
        onClick={handleSkip}
        className="text-zinc-700 text-xs uppercase tracking-widest hover:text-zinc-500 transition-colors py-2 px-4"
      >
        Continuar
      </button>
    </div>
  );

  // PROGRAMA: ESCANEO CORPORAL
  const renderEscaneoCorporal = () => {
    const currentPhrase = BODY_SCAN_PHRASES[currentBodyPart];
    
    return (
      <div className="relative z-10 flex flex-col items-center justify-center w-full max-w-md px-6">
        {/* Temporizador */}
        <div className="mb-8">
          <span className="text-5xl font-light text-zinc-400 tabular-nums">
            {formatTime(timeLeft)}
          </span>
        </div>

        {/* Indicador de parte del cuerpo */}
        <div className="mb-4 h-32 flex flex-col items-center justify-center">
          <p className="text-zinc-600 text-sm uppercase tracking-widest mb-3">
            {currentPhrase.part}
          </p>
          <p className="text-2xl font-light text-zinc-300 text-center animate-pulse">
            {currentPhrase.action}
          </p>
        </div>

        {/* Indicadores de progreso corporal */}
        <div className="flex gap-2 mb-12">
          {BODY_SCAN_PHRASES.map((_, idx) => (
            <div
              key={idx}
              className={`w-2 h-2 rounded-full transition-colors duration-500 ${
                idx === currentBodyPart ? 'bg-primary' : 'bg-zinc-800'
              }`}
            />
          ))}
        </div>

        {/* Botón saltar */}
        <button
          onClick={handleSkip}
          className="text-zinc-700 text-xs uppercase tracking-widest hover:text-zinc-500 transition-colors py-2 px-4"
        >
          Saltar descanso
        </button>
      </div>
    );
  };

  // PROGRAMA: MENTES INQUIETAS
  const renderMentesInquietas = () => (
    <div className="relative z-10 flex flex-col items-center justify-center w-full max-w-md px-6">
      {/* Mensaje principal */}
      <div className="mb-8 text-center">
        <p className="text-3xl font-bold text-zinc-300 mb-2">Quieto.</p>
        <p className="text-xl text-zinc-500">Sin móvil.</p>
      </div>

      {/* Temporizador grande y simple */}
      <div className="relative mb-8">
        <div className="w-48 h-48 rounded-full border-4 border-zinc-800 flex items-center justify-center">
          <span className="text-6xl font-light text-zinc-300 tabular-nums">
            {formatTime(timeLeft)}
          </span>
        </div>
        {/* Progreso circular sutil */}
        <svg className="absolute inset-0 w-48 h-48 transform -rotate-90" viewBox="0 0 100 100">
          <circle
            cx="50"
            cy="50"
            r="46"
            fill="none"
            stroke="#333"
            strokeWidth="2"
            strokeLinecap="round"
            strokeDasharray={`${progress * 2.89} 289`}
            className="transition-all duration-1000 ease-linear"
          />
        </svg>
      </div>

      {/* Indicador de respiración simplificado */}
      <div className="mb-12 text-center">
        <p className={`text-lg transition-all duration-500 ${
          breathPhase === 'inhale' ? 'text-zinc-400' : 'text-zinc-600'
        }`}>
          {breathPhase === 'inhale' ? '↑' : '↓'}
        </p>
      </div>

      {/* Botón saltar */}
      <button
        onClick={handleSkip}
        className="text-zinc-700 text-xs uppercase tracking-widest hover:text-zinc-500 transition-colors py-2 px-4"
      >
        Continuar
      </button>
    </div>
  );

  // PROGRAMA: RESPIRACIÓN 4-6 (por defecto)
  const renderRespiracion46 = () => (
    <div className="relative z-10 flex flex-col items-center justify-center w-full max-w-md px-6">
      {/* Indicador de serie */}
      <div className="mb-8 text-center">
        <p className="text-zinc-600 text-xs uppercase tracking-[0.3em] mb-1">
          Serie {seriesNumber} completada
        </p>
        <p className="text-zinc-500 text-sm">
          Preparando serie {nextSeriesNumber}
        </p>
      </div>

      {/* Círculo de respiración */}
      <div className="relative mb-8">
        <svg className="w-64 h-64 transform -rotate-90" viewBox="0 0 100 100">
          <circle cx="50" cy="50" r="45" fill="none" stroke="#1a1a1a" strokeWidth="2" />
          <circle
            cx="50"
            cy="50"
            r="45"
            fill="none"
            stroke="#333"
            strokeWidth="2"
            strokeLinecap="round"
            strokeDasharray={`${progress * 2.83} 283`}
            className="transition-all duration-1000 ease-linear"
          />
        </svg>

        <div className="absolute inset-0 flex items-center justify-center">
          <div 
            className={`rounded-full bg-zinc-900 border border-zinc-800 flex items-center justify-center transition-all ease-in-out ${
              breathPhase === 'inhale' ? 'w-40 h-40 scale-100' : 'w-32 h-32 scale-90'
            }`}
            style={{ transitionDuration: breathPhase === 'inhale' ? '4000ms' : '6000ms' }}
          >
            <div className="text-center">
              <span className="text-5xl font-light text-zinc-300 tabular-nums">
                {formatTime(timeLeft)}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Indicador de respiración */}
      <div className="mb-8 text-center h-16">
        <p className={`text-lg font-light tracking-wide transition-opacity duration-500 ${
          breathPhase === 'inhale' ? 'text-zinc-400' : 'text-zinc-500'
        }`}>
          {breathPhase === 'inhale' ? 'Inhala...' : 'Exhala...'}
        </p>
        <div className="flex items-center justify-center gap-2 mt-3">
          {[...Array(10)].map((_, i) => (
            <div 
              key={i}
              className={`w-1.5 h-1.5 rounded-full transition-all duration-300 ${
                breathPhase === 'inhale' && i < 4 ? 'bg-zinc-500' :
                breathPhase === 'exhale' && i >= 4 ? 'bg-zinc-600' : 'bg-zinc-800'
              }`}
            />
          ))}
        </div>
      </div>

      {/* Mensaje */}
      <p className="text-zinc-600 text-sm text-center max-w-xs leading-relaxed mb-12">
        Respira. Recupera. Observa tu cuerpo.
      </p>

      {/* Botón saltar */}
      <button
        onClick={handleSkip}
        className="text-zinc-700 text-xs uppercase tracking-widest hover:text-zinc-500 transition-colors py-2 px-4"
      >
        Saltar descanso
      </button>
    </div>
  );

  // Seleccionar renderizado según programa
  const renderProgram = () => {
    switch (program) {
      case 'silencio':
        return renderSilencio();
      case 'escaneo_corporal':
        return renderEscaneoCorporal();
      case 'mentes_inquietas':
        return renderMentesInquietas();
      case 'respiracion_4_6':
      default:
        return renderRespiracion46();
    }
  };

  return (
    <div 
      className="fixed inset-0 z-50 bg-black flex flex-col items-center justify-center"
      onClick={handleInteraction}
      style={{ touchAction: 'none' }}
      data-testid="rest-mode-container"
      data-program={program}
    >
      {/* Fondo */}
      <div className="absolute inset-0 bg-gradient-to-b from-zinc-950 via-black to-zinc-950" />
      
      {/* Contenido según programa */}
      {renderProgram()}

      {/* Barra de progreso inferior */}
      <div className="absolute bottom-0 left-0 right-0 h-1 bg-zinc-900">
        <div 
          className="h-full bg-zinc-700 transition-all duration-1000 ease-linear"
          style={{ width: `${progress}%` }}
        />
      </div>
    </div>
  );
};

export default RestMode;
